import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { Modal, ModalActions, ModalButton } from "./components/modal"

type FieldType = "text" | "email" | "phone" | "textarea" | "select" | "checkbox" | "date"

interface FormField {
  id: string
  type: FieldType
  label: string
  name: string
  placeholder?: string
  required: boolean
  options?: string[]
  position: number
}

interface Form {
  id: string
  code: string
  name: string
  description: string | null
  status: "draft" | "published" | "archived"
  fields: FormField[] | null
  submitButtonText: string
  successMessage: string
  submissionCount: number
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
]

function generateFieldName(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .substring(0, 30)
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("forms")
  const canMutate = useHasPermission("forms_mutate")
  const isNew = id === "new"

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<Partial<Form>>({
    name: "",
    description: "",
    fields: [],
    submitButtonText: "Submit",
    successMessage: "Thank you for your submission.",
  })

  // Field editing modal state
  const [editingField, setEditingField] = useState<FormField | null>(null)
  const [isAddingField, setIsAddingField] = useState(false)

  useEffect(() => {
    if (!isNew && id) {
      fetchForm()
    }
  }, [id, isNew])

  const fetchForm = async () => {
    try {
      const res = await client.api.admin.forms[":id"].$get({ param: { id: id! } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setForm(body.data as Form)
      } else {
        toast.error("Form not found")
        navigate("/admin/forms")
      }
    } catch {
      toast.error("Failed to load form")
      navigate("/admin/forms")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error("Form name is required")
      return
    }

    setSaving(true)
    try {
      if (isNew) {
        const res = await client.api.admin.forms.$post({
          json: { name: form.name.trim(), description: form.description || undefined },
        })
        const body = await res.json()
        if (res.ok && "data" in body) {
          const newForm = body.data as Form
          // If we have fields, update them
          if (form.fields && form.fields.length > 0) {
            await client.api.admin.forms[":id"].$patch({
              param: { id: newForm.id },
              json: {
                fields: form.fields,
                submitButtonText: form.submitButtonText,
                successMessage: form.successMessage,
              },
            })
          }
          toast.success("Form created")
          navigate(`/admin/forms/builder/${newForm.id}`)
        } else {
          toast.error(getErrorMessage(body))
        }
      } else {
        const res = await client.api.admin.forms[":id"].$patch({
          param: { id: id! },
          json: {
            name: form.name.trim(),
            description: form.description || null,
            fields: form.fields || [],
            submitButtonText: form.submitButtonText,
            successMessage: form.successMessage,
          },
        })
        const body = await res.json()
        if (res.ok && "data" in body) {
          setForm(body.data as Form)
          toast.success("Form saved")
          await fetchForm()
        } else {
          toast.error(getErrorMessage(body))
        }
      }
    } catch {
      toast.error("Failed to save form")
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!id || isNew) return

    try {
      const res = await client.api.admin.forms[":id"].publish.$post({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setForm(body.data as Form)
        toast.success("Form published")
        await fetchForm()
      } else {
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to publish form")
    }
  }

  const handleUnpublish = async () => {
    if (!id || isNew) return

    try {
      const res = await client.api.admin.forms[":id"].unpublish.$post({ param: { id } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setForm(body.data as Form)
        toast.success("Form unpublished")
        await fetchForm()
      } else {
        toast.error(getErrorMessage(body))
      }
    } catch {
      toast.error("Failed to unpublish form")
    }
  }

  const handleAddField = (field: Omit<FormField, "id" | "position">) => {
    const newField: FormField = {
      ...field,
      id: crypto.randomUUID(),
      position: (form.fields?.length || 0),
    }
    setForm((prev) => ({
      ...prev,
      fields: [...(prev.fields || []), newField],
    }))
    setIsAddingField(false)
  }

  const handleUpdateField = (updatedField: FormField) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((f) => (f.id === updatedField.id ? updatedField : f)),
    }))
    setEditingField(null)
  }

  const handleDeleteField = (fieldId: string) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || [])
        .filter((f) => f.id !== fieldId)
        .map((f, i) => ({ ...f, position: i })),
    }))
  }

  const handleMoveField = (fieldId: string, direction: "up" | "down") => {
    const fields = [...(form.fields || [])]
    const index = fields.findIndex((f) => f.id === fieldId)
    if (index === -1) return

    const newIndex = direction === "up" ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= fields.length) return

    const temp = fields[index]!
    fields[index] = fields[newIndex]!
    fields[newIndex] = temp

    // Update positions
    fields.forEach((f, i) => {
      f.position = i
    })

    setForm((prev) => ({ ...prev, fields }))
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Form Builder</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view forms.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/forms")}
          className="rounded-lg border border-gold-500 p-2 hover:bg-cream-100"
        >
          <ChevronLeftIcon className="h-5 w-5 text-ink" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-3xl">
            {isNew ? "Create Form" : "Edit Form"}
          </h1>
          {!isNew && form.code && (
            <p className="mt-1 text-sm text-ink/60">{form.code}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isNew && form.status === "published" && (
            <a
              href={`/forms/${form.code}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-gold-500 px-4 py-2.5 text-sm font-medium text-ink hover:bg-cream-100"
            >
              View public form
            </a>
          )}
          {canMutate && (
          <div className="flex gap-2">
            {!isNew && form.status === "draft" && (
              <button
                onClick={handlePublish}
                className="rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700"
              >
                Publish
              </button>
            )}
            {!isNew && form.status === "published" && (
              <button
                onClick={handleUnpublish}
                className="rounded-lg border border-gold-500 px-4 py-2.5 text-sm font-medium text-ink hover:bg-cream-100"
              >
                Unpublish
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-forest-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-forest-900 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Form Details */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">Form Details</h2>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Name *</label>
            <input
              type="text"
              value={form.name || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Contact Form"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Description</label>
            <textarea
              value={form.description || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="A brief description of this form"
              rows={2}
              className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Fields ({form.fields?.length || 0})</h2>
          {canMutate && (
            <button
              onClick={() => setIsAddingField(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-forest-700 px-3 py-2 text-sm font-medium text-forest-700 hover:bg-forest-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add Field
            </button>
          )}
        </div>

        {(!form.fields || form.fields.length === 0) ? (
          <div className="rounded-lg border border-dashed border-gold-500 p-8 text-center">
            <p className="text-sm text-ink/50">No fields yet. Add your first field to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {form.fields.map((field, index) => (
              <div
                key={field.id}
                className="flex items-center gap-3 rounded-lg border border-gold-500/50 bg-cream-50 p-4"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleMoveField(field.id, "up")}
                    disabled={index === 0}
                    className="rounded p-1 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleMoveField(field.id, "down")}
                    disabled={index === form.fields!.length - 1}
                    className="rounded p-1 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-ink">{field.label}</p>
                  <p className="text-xs text-ink/50">
                    {FIELD_TYPES.find((t) => t.value === field.type)?.label}
                    {field.required && " • Required"}
                    {field.placeholder && ` • "${field.placeholder}"`}
                  </p>
                </div>
                {canMutate && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingField(field)}
                      className="rounded p-2 hover:bg-white"
                    >
                      <EditIcon className="h-4 w-4 text-ink/50" />
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="rounded p-2 hover:bg-white"
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Submit Button Text</label>
            <input
              type="text"
              value={form.submitButtonText || "Submit"}
              onChange={(e) => setForm((prev) => ({ ...prev, submitButtonText: e.target.value }))}
              placeholder="Submit"
              className="w-full max-w-xs rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Success Message</label>
            <textarea
              value={form.successMessage || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, successMessage: e.target.value }))}
              placeholder="Thank you for your submission."
              rows={2}
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Submissions link */}
      {!isNew && form.submissionCount !== undefined && form.submissionCount > 0 && (
        <div className="rounded-[10px] border border-gold-500 bg-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-ink">Submissions</h2>
              <p className="text-sm text-ink/60">{form.submissionCount} submission{form.submissionCount !== 1 ? "s" : ""}</p>
            </div>
            <button
              onClick={() => navigate(`/admin/forms/builder/${id}/submissions`)}
              className="rounded-lg border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 hover:bg-forest-50"
            >
              View Submissions
            </button>
          </div>
        </div>
      )}

      {/* Add Field Modal */}
      <FieldEditorModal
        isOpen={isAddingField}
        onClose={() => setIsAddingField(false)}
        onSave={handleAddField}
      />

      {/* Edit Field Modal */}
      <FieldEditorModal
        isOpen={!!editingField}
        onClose={() => setEditingField(null)}
        field={editingField || undefined}
        onSave={(field) => handleUpdateField({ ...field, id: editingField!.id, position: editingField!.position })}
      />
    </div>
  )
}

function FieldEditorModal({
  isOpen,
  onClose,
  field,
  onSave,
}: {
  isOpen: boolean
  onClose: () => void
  field?: FormField
  onSave: (field: Omit<FormField, "id" | "position">) => void
}) {
  const [type, setType] = useState<FieldType>(field?.type || "text")
  const [label, setLabel] = useState(field?.label || "")
  const [name, setName] = useState(field?.name || "")
  const [placeholder, setPlaceholder] = useState(field?.placeholder || "")
  const [required, setRequired] = useState(field?.required || false)
  const [options, setOptions] = useState(field?.options?.join("\n") || "")

  // Reset form when field changes
  useEffect(() => {
    if (field) {
      setType(field.type)
      setLabel(field.label)
      setName(field.name)
      setPlaceholder(field.placeholder || "")
      setRequired(field.required)
      setOptions(field.options?.join("\n") || "")
    } else {
      setType("text")
      setLabel("")
      setName("")
      setPlaceholder("")
      setRequired(false)
      setOptions("")
    }
  }, [field, isOpen])

  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel)
    // Auto-generate name from label if name hasn't been manually edited
    if (!field) {
      setName(generateFieldName(newLabel))
    }
  }

  const handleSave = () => {
    if (!label.trim()) {
      toast.error("Field label is required")
      return
    }
    if (!name.trim()) {
      toast.error("Field name is required")
      return
    }
    if (type === "select" && !options.trim()) {
      toast.error("Dropdown options are required")
      return
    }

    const fieldData: Omit<FormField, "id" | "position"> = {
      type,
      label: label.trim(),
      name: name.trim(),
      placeholder: placeholder.trim() || undefined,
      required,
      options: type === "select" ? options.split("\n").map((o) => o.trim()).filter(Boolean) : undefined,
    }

    onSave(fieldData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={field ? "Edit Field" : "Add Field"}
      maxWidth="lg"
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Field Type</label>
          <Select
            value={FIELD_TYPES.find((t) => t.value === type)?.label || "Text"}
            onChange={(value) => {
              const newType = FIELD_TYPES.find((t) => t.label === value)?.value || "text"
              setType(newType)
            }}
            options={FIELD_TYPES.map((t) => t.label)}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Label *</label>
          <input
            type="text"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder="Email Address"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Field Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="email_address"
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
          <p className="mt-1 text-xs text-ink/50">Used internally to identify this field</p>
        </div>

        {type !== "checkbox" && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Placeholder</label>
            <input
              type="text"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
          </div>
        )}

        {type === "select" && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Options *</label>
            <textarea
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              placeholder="Option 1&#10;Option 2&#10;Option 3"
              rows={4}
              className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
            />
            <p className="mt-1 text-xs text-ink/50">One option per line</p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="required"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            className="h-4 w-4 rounded border-gold-500 text-forest-700 focus:ring-forest-700"
          />
          <label htmlFor="required" className="text-sm font-medium text-ink">
            Required field
          </label>
        </div>

        <ModalActions>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton onClick={handleSave} variant="primary">
            {field ? "Update" : "Add"} Field
          </ModalButton>
        </ModalActions>
      </div>
    </Modal>
  )
}
